import { Copy, FolderOpen } from '@orgus/ui/icons';
import { FACT_CELL } from './SessionFacts.tsx';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, IconButton, Tooltip, copyText, middleEllipsis, shortPath, toast } from '@orgus/ui';

export type Editor = 'vscode' | 'cursor';

/** The link an editor registers for opening a folder: vscode://file/C:/repo, forward slashes. */
export function editorUrl(path: string, editor: Editor): string {
  // The URI's path is the folder: its leading slash is the one after file.
  return `${editor}://file/${encodeURI(path.replace(/\\/g, '/').replace(/^\/+/, ''))}`;
}

/**
 * The folder as a fact's value: the path cut in the middle (its start and its telling end stay),
 * the full path in a tooltip, and a small menu to open it. Editor links open on this computer, so
 * they are offered only when the session runs here; Copy path works anywhere.
 */
export function FolderValue({ path, local, max = 40 }: { path: string; local: boolean; max?: number }) {
  return (
    <span className="flex min-w-0 items-center gap-1">
      <Tooltip content={<span className="break-all font-mono text-caption">{path}</span>}>
        <span tabIndex={0} className="min-w-0 truncate font-mono text-caption">
          {middleEllipsis(shortPath(path, max), max)}
        </span>
      </Tooltip>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton label="Open the folder" icon={<FolderOpen />} size="xs" className="shrink-0" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {local && (
            <>
              <DropdownMenuItem icon={<FolderOpen />} onSelect={() => window.open(editorUrl(path, 'vscode'), '_self')}>
                Open in VS Code
              </DropdownMenuItem>
              <DropdownMenuItem icon={<FolderOpen />} onSelect={() => window.open(editorUrl(path, 'cursor'), '_self')}>
                Open in Cursor
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuItem icon={<Copy />} onSelect={() => void copyText(path).then((ok) => ok && toast.success('Path copied'))}>
            Copy path
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}

/** The session's folder as one fact cell of a FACT_GRID: the tiny label over FolderValue. */
export function FolderFact({ path, local }: { path: string; local: boolean }) {
  return (
    <div className={FACT_CELL}>
      <dt className="text-caption text-fg-subtle">Folder</dt>
      <dd className="flex h-5 min-w-0 items-center text-fg-muted">
        <FolderValue path={path} local={local} />
      </dd>
    </div>
  );
}
